<template>
  <!-- belegliste: helle Zeilen wie in den Listen der alten App -->
  <div class="belegliste">
    <div class="d-flex align-items-center flex-wrap mb-2">
      <span v-if="!ohneAenderungZeigen && anzahlOhneAenderung > 0" class="kopf mr-3">
        {{ $t(anzahlOhneAenderung === 1 ? "global.runhiddenwithoutchanges" : "global.runshiddenwithoutchanges", { count: anzahlOhneAenderung }) }}
      </span>
      <div class="custom-control custom-checkbox">
        <input :id="schalterId" v-model="ohneAenderungZeigen" type="checkbox" class="custom-control-input" />
        <label :for="schalterId" class="custom-control-label">{{ $t("global.showrunswithoutchanges") }}</label>
      </div>
    </div>

    <b-table
      small
      hover
      tbody-tr-class="item zeile"
      :items="laeufe"
      :fields="tabellenFelder"
      primary-key="id"
      show-empty
      :empty-text="$t('global.noruns')"
      @row-clicked="oeffnen"
    >
      <template #cell(ablauf)="{ item }">{{ ablaufText(item) }}</template>
      <template #cell(laufart)="{ item }">{{ item.laufartBezeichnung || item.laufart }}</template>
      <template #cell(art)="{ item }">{{ artText(item, uebersetze) }}</template>
      <template #cell(ausloeser)="{ item }">{{ ausloeserText(item, uebersetze) }}</template>
      <template #cell(dauer)="{ item }">{{ dauerText(item, uebersetze) }}</template>
      <template #cell(ergebnis)="{ item }">
        <span :class="ergebnisKlasse(item)">{{ ergebnisText(item, uebersetze) }}</span>
      </template>
      <template #cell(zusammenfassung)="{ item }">
        <span :class="{ 'text-muted': zusammenfassungIstMeldung(item) }">{{ zusammenfassungText(item) }}</span>
      </template>
      <template #cell(anzahlFehler)="{ item }">
        <span :class="{ 'text-rot': item.anzahlFehler > 0 }">{{ item.anzahlFehler }}</span>
      </template>
      <template #cell(id)="{ item }"><span class="mono">{{ item.id }}</span></template>
    </b-table>
    <infinite-loading :identifier="infiniteId" @infinite="nachladen">
      <div slot="spinner">{{ $t("global.loadingmoreruns") }}</div>
      <div slot="no-more"></div>
      <div slot="no-results"></div>
    </infinite-loading>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import { BTable } from "bootstrap-vue";
import InfiniteLoading from "vue-infinite-loading";
import { uuidv4 } from "@/utils/Uuid";
import {
  ablaufText,
  artText,
  ausloeserText,
  dauerText,
  ergebnisKlasse,
  ergebnisText,
  uebersetzt,
  zusammenfassungIstMeldung,
  zusammenfassungText,
} from "@/components/Lauf/LaufTexte";

/** Seitengrösse von GET lauf/ (LaufService.SEITENGROESSE) */
const SEITENGROESSE = 50;

/** Die Spalten, aus denen eine Seite mit [felder] wählt (9.3, 9.4) */
const SPALTEN = {
  zeitpunkt: { key: "angefordertAm", label: "Zeitpunkt", labelSchluessel: "global.time" },
  ablauf: { key: "ablauf", label: "Ablauf und Laufart", labelSchluessel: "global.workflowandruntype" },
  laufart: { key: "laufart", label: "Laufart", labelSchluessel: "global.runtype" },
  art: { key: "art", label: "Art", labelSchluessel: "global.kind" },
  ausloeser: { key: "ausloeser", label: "Auslöser", labelSchluessel: "global.trigger" },
  dauer: { key: "dauer", label: "Dauer", labelSchluessel: "global.duration" },
  ergebnis: { key: "ergebnis", label: "Ergebnis", labelSchluessel: "global.result" },
  zusammenfassung: { key: "zusammenfassung", label: "Zusammenfassung", labelSchluessel: "global.summary" },
  fehler: { key: "anzahlFehler", label: "Fehler", labelSchluessel: "global.error" },
  id: { key: "id", label: "Lauf-ID", labelSchluessel: "global.runid" },
};

/**
 * Tabelle der Läufe aus GET lauf/ für das Protokoll (9.4) und die Seite je Ablauf (9.3): seitenweise mit
 * Nachladen, Läufe ohne Änderung ausgeblendet mit Zähler und Schalter. Jede Zeile öffnet die Lauf-Ansicht über
 * den Event-Bus; die Seite bindet dafür <lauf-ansicht /> ein.
 *
 * - filter: Parameter für GET lauf/ wie art, ablauf, status, ausloeser, test, id, code, von, bis; leere fehlen.
 *   Ändert er sich, lädt die Tabelle von vorn.
 * - felder: die Spalten, Schlüssel aus SPALTEN.
 * Meldet nach jeder Seite „geladen“ mit gesamtanzahl, für die Zahl im HeadMenu.
 */
export default {
  name: "LaufTabelle",
  components: { BTable, InfiniteLoading },
  props: {
    filter: { type: Object, required: true },
    felder: { type: Array, required: true },
  },
  data() {
    return {
      schalterId: "ohne-aenderung-" + uuidv4(),
      ohneAenderungZeigen: false,
      laeufe: [],
      seite: 0,
      anzahlOhneAenderung: 0,
      infiniteId: +new Date(),
    };
  },
  computed: {
    tabellenFelder() {
      // In computed, damit die Überschriften mit der Sprache wechseln
      return this.felder.map((f) => ({ key: SPALTEN[f].key, label: uebersetzt(this.uebersetze, SPALTEN[f], "label") }));
    },
  },
  watch: {
    filter: {
      handler() {
        this.vonVorn();
      },
      deep: true,
    },
    ohneAenderungZeigen() {
      this.vonVorn();
    },
  },
  methods: {
    ablaufText,
    artText,
    ausloeserText,
    dauerText,
    ergebnisKlasse,
    ergebnisText,
    zusammenfassungIstMeldung,
    zusammenfassungText,
    uebersetze(schluessel) {
      return this.$t(schluessel);
    },
    vonVorn() {
      this.laeufe = [];
      this.seite = 0;
      this.infiniteId += 1;
    },
    nachladen($state) {
      const infiniteId = this.infiniteId;
      const params = { seite: this.seite };
      Object.entries(this.filter).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") params[k] = v;
      });
      if (this.ohneAenderungZeigen) params.ohneAenderung = "ja";

      AppApi.get("lauf/", { params })
        .then((response) => {
          // Eine Antwort zu einem inzwischen geänderten Filter verwerfen
          if (infiniteId !== this.infiniteId) return;
          const daten = response.data;
          this.laeufe.push(...daten.laeufe);
          this.anzahlOhneAenderung = daten.anzahlOhneAenderung;
          this.seite += 1;
          this.$emit("geladen", { gesamtanzahl: daten.gesamtanzahl });
          if (daten.laeufe.length < SEITENGROESSE) $state.complete();
          else $state.loaded();
        })
        .catch((e) => {
          if (infiniteId !== this.infiniteId) return;
          this.$notify(apiErrorToAlert(e));
          $state.error();
        });
    },
    oeffnen(lauf) {
      this.$bus.$emit("lauf-ansicht-oeffnen", { lauf: lauf.id });
    },
  },
};
</script>

<style lang="scss" scoped>
.kopf {
  font-size: 13px;
  color: #56616b;
}
.mono {
  font-family: Consolas, Menlo, monospace;
  font-size: 12.5px;
}
::v-deep .zeile {
  cursor: pointer;
}
.text-bernstein {
  color: #a54800;
  font-weight: 600;
}
.text-rot {
  color: #ae2e24;
  font-weight: 600;
}
</style>
